const MIN_NAME_LETTERS = 2;
const NAME_LETTER_REGEX = /\p{L}/gu;

/**
 * Nombre de quien consulta: al menos 2 letras. “34343” no pasa.
 * @param {string} value
 * @returns {boolean}
 */
export function isValidCollectionInquiryName(value) {
  const trimmed = String(value || '').trim();
  if (trimmed.length < MIN_NAME_LETTERS) return false;
  const letters = trimmed.match(NAME_LETTER_REGEX);
  return Boolean(letters && letters.length >= MIN_NAME_LETTERS);
}

function readId(value) {
  return String(value || '').trim();
}

function readLabel(value) {
  const text = String(value || '').trim();
  return text || '';
}

/**
 * ¿El lote catastral es ítem de la lista (`item_type: 'lot'`), no solo padre de UFs?
 * @param {Array<object>|null|undefined} flatRows
 * @param {string|null|undefined} lotId
 * @returns {boolean}
 */
export function sharedListHasLotItem(flatRows, lotId) {
  const key = readId(lotId);
  if (!key) return false;
  return (Array.isArray(flatRows) ? flatRows : []).some((row) => {
    if (String(row?.lot_id || '') !== key) return false;
    if (row?.item_type === 'lot') return true;
    // Filas legacy sin item_type: lote directo = sin user_lot_id / id de UF.
    if (row?.item_type === 'unit' || row?.item_type === 'lot_content') return false;
    return row?.user_lot_id == null && row?.id == null;
  });
}

/**
 * `lot_user_content` que la lista compartida expone para ese lote
 * (`GET /getsharedlist`: grouped `lots[].lot_user_content`, flat en el ítem de
 * la UF; las filas aplanadas lo traen igual). `null` = no se expone → el lote
 * padre de una UF no se puede consultar (API 68b290a). No mira `has_content`.
 *
 * @param {Array<object>|null|undefined} flatRows
 * @param {string|null|undefined} lotId
 * @returns {object|null}
 */
export function getSharedListLotUserContent(flatRows, lotId) {
  const key = readId(lotId);
  if (!key) return null;
  const row = (Array.isArray(flatRows) ? flatRows : []).find((r) => (
    String(r?.lot_id || '') === key && r?.lot_user_content != null
  ));
  return row ? row.lot_user_content : null;
}

/**
 * Contexto de la consulta en el panel del lote (lista y lote compartidos, misma
 * regla con 1 o N UFs):
 * - pestaña Lote (`activeTab` 0) → consulta por el lote;
 * - Unidades Funcionales con una UF abierta (detalle) → por esa UF;
 * - Unidades Funcionales en el listado → sin form ni botón “Consultar”.
 * La UF auto-seleccionada o la de la URL no cuentan fuera de su detalle.
 *
 * @param {{ activeTab?: number, showDetailsTab?: boolean, unitView?: string,
 *   showUnitsDetailPane?: boolean }} [input]
 * @returns {{ allowed: boolean, unitOpen: boolean }}
 */
export function resolveInquiryPanelContext({
  activeTab = 0,
  showDetailsTab = false,
  unitView = 'list',
  showUnitsDetailPane = false,
} = {}) {
  const unitOpen = Boolean(showDetailsTab && activeTab === 1 && unitView === 'detail' && showUnitsDetailPane);
  return { allowed: activeTab === 0 || unitOpen, unitOpen };
}

/**
 * Destino de consulta en lista compartida.
 * Solo consultar con UF abierta o con lote privado abierto que sea ítem `lot`.
 * No adivinar user_lot_id si hay una sola UF sin abrirla.
 * Nunca mandar lot_id del padre si el lote no es ítem de la lista.
 *
 * @param {{
 *   openUserLotId?: string|null,
 *   lotId?: string|null,
 *   lotInList?: boolean,
 *   unitLabel?: string|null,
 *   lotLabel?: string|null,
 * }} [input]
 * @returns {{
 *   canShow: boolean,
 *   userLotId: string,
 *   lotId: string,
 *   parcelLabel: string,
 * }}
 */
export function resolveSharedListInquiryTarget({
  openUserLotId = null,
  lotId = null,
  lotInList = false,
  unitLabel = null,
  lotLabel = null,
} = {}) {
  const userLotId = readId(openUserLotId);
  if (userLotId) {
    return {
      canShow: true,
      userLotId,
      lotId: '',
      parcelLabel: readLabel(unitLabel) || 'esta unidad',
    };
  }

  const trimmedLotId = readId(lotId);
  if (lotInList && trimmedLotId) {
    return {
      canShow: true,
      userLotId: '',
      lotId: trimmedLotId,
      parcelLabel: readLabel(lotLabel) || trimmedLotId,
    };
  }

  return {
    canShow: false,
    userLotId: '',
    lotId: '',
    parcelLabel: '',
  };
}

/**
 * F3 — destino de consulta en un lote / UF compartido solo
 * (`/#/shared-user-lot/<token>`, sin lista). La parcela siempre se ve en el
 * share, así que siempre se puede consultar por el lote. Con una UF del
 * share abierta va su `user_lot_id`; una UF que no está en el snapshot del
 * share (`units[]`) no se manda (la API respondería `ITEM_NOT_IN_SHARE`).
 *
 * @param {{
 *   openUserLotId?: string|null,
 *   shareUnits?: Array<object>|null,
 *   unitLabel?: string|null,
 *   lotLabel?: string|null,
 * }} [input]
 * @returns {{ canShow: boolean, userLotId: string, parcelLabel: string }}
 */
export function resolveSharedLotInquiryTarget({
  openUserLotId = null,
  shareUnits = null,
  unitLabel = null,
  lotLabel = null,
} = {}) {
  const openId = readId(openUserLotId);
  const unit = openId
    ? (Array.isArray(shareUnits) ? shareUnits : []).find((row) => (
      readId(row?.user_lot_id) === openId || readId(row?.id) === openId
    ))
    : null;
  if (unit) {
    return {
      canShow: true,
      userLotId: readId(unit.user_lot_id) || readId(unit.id),
      parcelLabel: readLabel(unitLabel) || 'esta unidad',
    };
  }
  return {
    canShow: true,
    userLotId: '',
    parcelLabel: readLabel(lotLabel) || 'este lote',
  };
}

/**
 * `viewer_can_manage` (SQL 099) en la raíz de `getsharedlist`,
 * `getsharedlotdetail`, `getshareduserlot` y `getcadproject` público: `true`
 * para dueño / editores (lista, CAD o espacio); `false` anónimo o JWT inválido.
 * Tolera que venga dentro de `data`. Ausente (API vieja) → false.
 *
 * @param {object|null|undefined} res
 * @returns {boolean}
 */
export function readViewerCanManage(res) {
  return res?.viewer_can_manage === true || res?.data?.viewer_can_manage === true;
}
